<script lang="ts" module>
	import type { EmblaCarouselType, EmblaOptionsType, EmblaPluginType } from "embla-carousel";
	import emblaCarouselSvelte from "embla-carousel-svelte";
	import { WheelGesturesPlugin as wheelGestures } from "embla-carousel-wheel-gestures";
	import type { Snippet } from "svelte";
	import type { ClassValue } from "svelte/elements";
	import { lerp } from "@core/utils/lerp.js";

	function gridContainerAlign(viewSize: number): number {
		let containerWidth: string | number = getComputedStyle(document.documentElement).getPropertyValue(
			"--container-width",
		);
		if (containerWidth === "100%") {
			containerWidth = viewSize;
		} else {
			containerWidth = parseInt(containerWidth);
		}
		return (viewSize - containerWidth) / 2;
	}
</script>

<script lang="ts" generics="Item">
	let {
		api = $bindable(),
		canNext = $bindable(false),
		canPrev = $bindable(false),
		/* eslint-disable prefer-const */
		alignWithGridContainer = false,
		class: className,
		item,
		items,
		itemsContainerClass,
		options = {},
		plugins = [],
		type = "flex",
		/* eslint-enable prefer-const */
	}: {
		api?: EmblaCarouselType;
		alignWithGridContainer?: boolean;
		canNext?: boolean;
		canPrev?: boolean;
		class?: ClassValue;
		item: Snippet<[item: Item, index: number, isDuplicate: boolean]>;
		items: Item[];
		itemsContainerClass?: ClassValue;
		options?: EmblaOptionsType;
		plugins?: EmblaPluginType[];
		type?: "flex" | "grid";
	} = $props();

	let duplicateLoopTimes = $state(1);
	function onemblaInit(event: CustomEvent<EmblaCarouselType>): void {
		api = event.detail;
		const biggestSlide = api
			.slideNodes()
			.reduceRight((prev, curr) => (curr.clientWidth > prev.clientWidth ? curr : prev));
		const possibleSlideAmount = Math.ceil(api.containerNode().clientWidth / biggestSlide.clientWidth);
		duplicateLoopTimes = Math.ceil(
			items.length <= possibleSlideAmount ? lerp(possibleSlideAmount + 1, 2, items.length / possibleSlideAmount) : 1,
		);

		// Ensure looping still works for long/variable-width slides.
		// If loop is enabled and we have multiple items, duplicate at least twice
		// so Embla has enough slides to create a seamless infinite scroll.
		if (options.loop && items.length > 1 && duplicateLoopTimes < 2) {
			duplicateLoopTimes = 2;
		}
	}

	function updateButtons(api: EmblaCarouselType): void {
		canNext = api.canScrollNext();
		canPrev = api.canScrollPrev();
	}

	$effect(() => {
		const apiInstance = api;

		if (apiInstance) {
			const listener = updateButtons.bind(null, apiInstance);

			listener();
			apiInstance.on("resize", listener);
			apiInstance.on("reInit", listener);
			apiInstance.on("init", listener);
			apiInstance.on("select", listener);

			return (): void => {
				apiInstance.off("resize", listener);
				apiInstance.off("reInit", listener);
				apiInstance.off("init", listener);
				apiInstance.off("select", listener);
			};
		}

		canNext = false;
		canPrev = false;

		return void 0;
	});

	function isScrollable(api: EmblaCarouselType): boolean {
		return api.internalEngine().scrollSnaps.length > 1;
	}

	const duplicatedItems = $derived(
		options.loop ? Array.from({ length: duplicateLoopTimes }).flatMap(() => items) : items,
	);

	// How to use:
	// https://www.embla-carousel.com/get-started/svelte/
	// https://www.embla-carousel.com/guides/
</script>

<div
	class={["overflow-hidden", className]}
	use:emblaCarouselSvelte={{
		options: {
			duration: 20,
			inViewThreshold: 0.5,
			watchDrag: isScrollable,
			...(alignWithGridContainer && {
				align: gridContainerAlign,
			}),
			...options,
		},
		plugins: [...plugins, wheelGestures()],
	}}
	onwheel={(event: WheelEvent): void => {
		if (event.shiftKey && event.deltaX === 0 && event.deltaY !== 0) {
			event.target?.dispatchEvent(
				new WheelEvent("wheel", { deltaX: event.deltaY, deltaY: 0, shiftKey: false, bubbles: true }),
			);
		}
	}}
	{onemblaInit}
>
	<div
		class={[
			"min-h-[inherit] h-full [&>*]:min-w-0 [&>*]:grow-0 [&>*]:shrink-0 [&>*]:max-w-full",
			alignWithGridContainer && "mx-[max(calc((100%-var(--container-width))/2),var(--container-gap))]",
			type === "flex" && options.axis === "y" && "flex-col",
			itemsContainerClass,
			type,
		]}
	>
		{#each duplicatedItems as it, index (index)}
			{@render item(it, index, index >= items.length)}
		{/each}
	</div>
</div>
